/**
 * The browser Sentry config, as a function so each entry decides WHEN it runs:
 * the app shell (instrument.js) runs it first thing, and the isolated P7
 * /auth/handoff page runs it only after it has cleared its #h fragment
 * (docs/auth/handoff-contract.md §5).
 */
import { hireSentryEvent, hireTelemetry } from "./hirePrivacy";
import * as Sentry from "@sentry/react";
import { scrubTokenSentryEvent, scrubTokenBreadcrumb } from "./scrubTokens";

export function initSentry(): void {
  const isProduction = import.meta.env.PROD;

  Sentry.init({
    dsn: "https://893883f6751b88fbb096fa485a438d6b@o4510643459981312.ingest.us.sentry.io/4510643562020865",

    // Proxy Sentry requests through our server to avoid ad blockers (production only)
    ...(isProduction && { tunnel: "/tunnel" }),

    // Keep document-share capability tokens (a 32-byte hex secret in the URL path)
    // out of error events, transaction names, and breadcrumbs. sendDefaultPii +
    // full tracing would otherwise ship the raw /share/:token URL to Sentry.
    beforeSend: event => hireSentryEvent(scrubTokenSentryEvent(event)),
    beforeSendTransaction: event => hireTelemetry(scrubTokenSentryEvent(event)),
    beforeBreadcrumb: crumb => hireTelemetry(scrubTokenBreadcrumb(crumb)),
    beforeSendLog: log => hireTelemetry(log),

    integrations: [Sentry.browserTracingIntegration()],

    // Adds request headers and IP for users, for more info visit:
    // https://docs.sentry.io/platforms/javascript/guides/react/configuration/options/#sendDefaultPii
    sendDefaultPii: true,

    // Enable logs to be sent to Sentry
    enableLogs: true,

    environment: import.meta.env.MODE,
    enabled: import.meta.env.PROD, // Only enable in production

    // Browser-extension noise, not app code (Sentry HOLLYWOODTRUCKS-API-6):
    // WebExtension APIs rejecting inside content scripts surface as unhandled
    // rejections on our pages.
    ignoreErrors: [
      /runtime\.sendMessage\(\)/,
      /Extension context invalidated/,
    ],

    // Capture 100% of transactions for performance monitoring
    tracesSampleRate: 1.0,

    // Enable distributed tracing for these URLs
    tracePropagationTargets: ["localhost", /^https:\/\/.*\.hollywoodtrucks\.com\/api/],
  });
}
